/**
 * Projectile — the RUN-AND-GUN bullet (the one genuinely-new sim primitive).
 *
 * A runtime-spawned entity (never placed in level.entities[] → out of computeLevelId) that
 * travels along a fixed velocity and despawns on the FIRST solid tile / off-world / a timeout.
 * Its `owner` picks the mirror behaviour:
 *   'player' (default): KILLS the stomp-set enemies it overlaps, routed through
 *     ctx.killEnemiesInBox → the SAME ctx.killEntity path a stomp/sword uses (chipping an
 *     ARMORED's hp while >1). Harmless to the player. The engine's first entity-vs-entity kill.
 *   'enemy' (a GUNNER's shot): costs the PLAYER a heart on overlap (via onPlayerOverlap in the
 *     World overlap pass) + despawns; never kills an enemy.
 *
 * Determinism: pure integer step counter (life) + `v*dt` float motion + integer tile-cell
 * math (Math.floor). NO random / wall-clock / transcendentals → ESLint-clean under src/engine/.
 * A recorded run re-emits identical bullets (the fire press, aim, spawn queue, and this motion
 * are all deterministic).
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';

/** Bullet body/render size (px). A small AABB — kept a constant for v1 (the handoff's ~16×8;
 *  widened slightly so a fast bullet reliably overlaps an enemy body in a single step). The
 *  Sim reads {@link PROJECTILE_H} to place the muzzle spawn's feetY so the body centers there. */
export const PROJECTILE_W = 22;
export const PROJECTILE_H = 10;
/** Extra px OUTSIDE the world bounds before an off-screen bullet despawns (so a bullet fired
 *  at the very edge doesn't die the instant it clears the wall). */
const OFFWORLD_MARGIN = 24;

export class Projectile implements Entity {
  readonly type = EntityType.PROJECTILE;
  body: AABB;
  vx: number;
  vy: number;
  facing: 1 | -1;
  alive = true;

  /** Remaining life in fixed steps (integer → deterministic). At 0 the bullet despawns. */
  private life: number;
  /**
   * Who fired it. 'player' (default) kills enemies + is harmless to the player; 'enemy' is the
   * mirror (hurts the player on overlap, never kills an enemy). Both share all despawn rules.
   */
  private readonly owner: 'player' | 'enemy';
  /** Optional downward accel (px/s²) → an ARCING lob (a LOBBER's grenade). 0 = straight (bullet). */
  private readonly grav: number;
  /** Optional sprite-key override (e.g. 'grenade') + render size; default = the bullet pellet. */
  private readonly sprite: string | null;
  private readonly rw: number;
  private readonly rh: number;

  /**
   * @param cx     spawn center-x (the muzzle x, world px)
   * @param feetY  bottom of the bullet's cell — muzzleCy + PROJECTILE_H/2, so the body centers
   *               on the muzzle (the same feetY convention the spawn factory uses)
   * @param props  `vx`/`vy` (px/s), `life` (projectileLifeSteps), `owner` ('player'|'enemy')
   */
  constructor(cx: number, feetY: number, props: Record<string, unknown> | undefined) {
    this.vx = typeof props?.vx === 'number' ? props.vx : 0;
    this.vy = typeof props?.vy === 'number' ? props.vy : 0;
    this.life = typeof props?.life === 'number' ? Math.floor(props.life) : 0;
    this.owner = props?.owner === 'enemy' ? 'enemy' : 'player';
    this.grav = typeof props?.gravity === 'number' ? props.gravity : 0;
    this.sprite = typeof props?.sprite === 'string' ? props.sprite : null;
    this.rw = typeof props?.rw === 'number' ? props.rw : PROJECTILE_W + 8;
    this.rh = typeof props?.rh === 'number' ? props.rh : PROJECTILE_H + 6;
    this.facing = this.vx < 0 ? -1 : 1; // for the render flip
    this.body = { x: cx - PROJECTILE_W / 2, y: feetY - PROJECTILE_H, w: PROJECTILE_W, h: PROJECTILE_H };
  }

  update(dt: number, ctx: EntityContext): void {
    // Arc (a LOBBER grenade): gravity bends the trajectory down. grav=0 → straight (a bullet).
    this.vy += this.grav * dt;
    // Move along the (possibly arcing) velocity.
    this.body.x += this.vx * dt;
    this.body.y += this.vy * dt;

    const cx = this.body.x + this.body.w / 2;
    const cy = this.body.y + this.body.h / 2;
    const grid = ctx.grid;

    // Solid tile → despawn (a bullet dies on the first solid cell; it does NOT collide-and-stop
    // like the player). Point-solid check at the bullet's center.
    if (grid.isSolid(Math.floor(cx / grid.ts), Math.floor(cy / grid.ts))) { this.alive = false; return; }

    // Off-world → despawn (a missed shot must not leak an entity forever).
    if (cx < -OFFWORLD_MARGIN || cx > grid.widthPx + OFFWORLD_MARGIN
        || cy < -OFFWORLD_MARGIN || cy > grid.heightPx + OFFWORLD_MARGIN) { this.alive = false; return; }

    // Timeout → despawn.
    this.life--;
    if (this.life <= 0) { this.alive = false; return; }

    // PLAYER bullet: enemy hit → kill/chip + despawn. killEnemiesInBox reuses combat.isAttackKillable
    // (the stomp set), routing each through ctx.killEntity — so a bullet kills EXACTLY what a stomp
    // kills (chipping an ARMORED's hp instead of killing while hp>1). One connect, one despawn. An
    // ENEMY bullet skips this (it never kills an enemy) — it damages the player via onPlayerOverlap.
    if (this.owner === 'player' && ctx.killEnemiesInBox(this.body)) { this.alive = false; return; }
  }

  /**
   * A PLAYER bullet never hurts the player (it spawns overlapping the muzzle). An ENEMY bullet
   * costs the player a heart (a no-op during i-frames) and despawns on contact so it can't
   * multi-hit through the i-frame window.
   */
  onPlayerOverlap(ctx: EntityContext): void {
    if (this.owner === 'enemy') { ctx.hurtPlayer(); this.alive = false; }
  }

  spriteKey(): string { return this.sprite ?? (this.owner === 'enemy' ? 'bullet_enemy' : 'bullet'); }
  visible(): boolean { return this.alive; }
  /** Explicit render size — the tiny AABB would otherwise scale a 64² sprite to nothing. A grenade
   *  overrides to a square via props.rw/rh; a bullet uses the default pellet size. */
  renderSize(): { w: number; h: number } { return { w: this.rw, h: this.rh }; }
}
